import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { Divider, Flex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import FadeDiv from '@/components/Common/FadeDiv';
import ScrollView from '@/components/Common/ScrollView';
import Tooltip from '@/components/Tooltip/TooltipV2';
import useAgentStore from '@/features/Agent/stores';
import useTrackModal from '@/hooks/domain/useTrackModal';
import useUserRole from '@/hooks/domain/useUserRole';
import useKeymap from '@/hooks/pure/useKeymap';
import type { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { detailModalStyle } from '@/styles/style';

import AgentDetailTemplate from './AgentDetailTemplate';

type Props = {
  agentAlias: string;
  pos: number;
  closeModal: () => void;
  onSelectNewModal: (alias: string, type: DETAIL_MODAL_TYPE) => void;
};

const AgentDetailModal = (props: Props) => {
  const agentMap = useAgentStore((state) => state.agentMap);
  const agent = agentMap[props.agentAlias];
  const removeAgent = useAgentStore((state) => state.removeAgent);
  const { isEditor } = useUserRole();
  const setPopup = usePopup();
  const addToast = useToast();
  const deleteAgent = () => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Delete agent',
      message: 'Are you sure you want to delete this agent?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        if (!agent) return;
        removeAgent(agent.alias);
        props.closeModal();
        addToast('Agent Deleted', 'success');
      },
    });
  };

  useTrackModal({
    domain: 'agents',
    alias: props.agentAlias,
  });
  useKeymap('Escape', props.closeModal);

  return (
    <>
      <ModalStyle status={true} pos={props.pos}>
        <Flex j="space-between" a="center" style={{ padding: '16px 32px' }}>
          <Flex></Flex>
          <Flex g="8px">
            {isEditor && (
              <Tooltip tooltipNode="Delete the agent">
                <IconButton icon={<DeleteOutlined />} onClick={deleteAgent} />
              </Tooltip>
            )}
            <IconButton
              icon={<CloseOutlined />}
              onClick={props.closeModal}
              tooltip="Close"
              shortcut="ESC"
            />
          </Flex>
        </Flex>

        <Divider direction="h" />
        <ScrollView>
          <AgentDetailTemplate
            agent={agent}
            closeModal={props.closeModal}
            onSelectNewModal={props.onSelectNewModal}
          />
        </ScrollView>
      </ModalStyle>
    </>
  );
};

const ModalStyle = styled(FadeDiv)<{ pos: number }>`
  position: fixed;
  width: 600px;
  height: 100vh;
  top: 0;
  right: ${({ pos }) => pos}px;

  ${detailModalStyle}

  .feedback-list-area {
    padding: 24px 32px;
  }
`;

export default AgentDetailModal;
